import { useMemo } from 'react';
import { getName, isEmptyValue } from '@home/utils/common';
import { swapPositions } from '@/pages/dashboard/components/Chart/_config/utils';
import { getEchartTooltipString } from '@/pages/dashboard/components/EchartsTooltip';

export const useChartData = ({
  data,
  currentTab,
  selectedSegment,
  isEN,
  competitorList,
  formatRule,
  lastOriginData,
  dateMap,
  tooltipDateMap,
  legendData,
  colorMap,
  tooltipConfig,
  dateNameMap,
  visibleSeries
}: any) => {
  // 生成 series 数据
  const series = useMemo(() => {
    if (!data?.list?.length || !currentTab?.componentMetrics) {
      return [];
    }

    const { list } = data;
    
    if (selectedSegment === 'forecast') {
      return currentTab.componentMetrics.map((metric: any) => {
        const name = getName(isEN, metric.enName, metric.cnName);
        const seriesData = list.map((item: any) => {
          const value = item.metrics[metric.metricKey]?.value;
          return value === "" || value === null || value === undefined ? "" : value;
        });
        
        return {
          name,
          type: "line",
          connectNulls: true,
          data: seriesData,
          itemStyle: { color: metric.color },
          lineStyle: { color: metric.color, width: 2 },
          symbol: 'circle',
          symbolSize: 4,
          smooth: false
        };
      });
    } 
    
    if (selectedSegment === 'competitor') {
      const series: any[] = [];

      // 添加current_sales线
      const currentSalesMetric = currentTab.componentMetrics.find((m: any) => m.metricKey === 'current_sales');
      if (currentSalesMetric) {
        const name = getName(isEN, currentSalesMetric.enName, currentSalesMetric.cnName);
        const seriesData = list.map((item: any) => {
          const value = item.metrics.current_sales?.value;
          return value === "" || value === null || value === undefined ? "" : value;
        });

        series.push({
          name,
          type: "line",
          connectNulls: true,
          data: seriesData,
          itemStyle: { color: currentSalesMetric.color },
          lineStyle: { color: currentSalesMetric.color, width: 2 },
          symbol: 'circle',
          symbolSize: 4,
          smooth: false
        });
      }

      // 添加竞品数据线
      if (competitorList?.length) {
        const firstItem = data.list[0];
        const availableMetrics = Object.keys(firstItem.metrics || {});

        availableMetrics.forEach((metricKey) => {
          const competitor = competitorList.find((comp: any) => comp.combined_id === metricKey);
          if (competitor) {
            const competitorName = competitor.game_name;
            const seriesData = list.map((item: any) => {
              const value = item.metrics[metricKey]?.value;
              return value === "" || value === null || value === undefined ? "" : value;
            });

            series.push({
              name: competitorName,
              type: "line",
              connectNulls: true,
              data: seriesData,
              symbol: 'circle',
              symbolSize: 4,
              smooth: false
            });
          }
        });
      }

      return series;
    }

    return [];
  }, [data, currentTab?.componentMetrics, selectedSegment, isEN, competitorList]);

  // 浮窗处理
  const formatterFunc = (params: any[]) => {
    if (!params || params.length === 0) return '';

    const axisValue = params[0]?.axisValue || params[0]?.name;
    
    // 浮窗隐藏（无x轴刻度隐藏浮窗）
    if (!dateMap[axisValue]) {
      return '';
    }

    // 找到current_sales系列的名称
    const currentSalesMetric = currentTab?.componentMetrics?.find((m: any) => m.metricKey === 'current_sales');
    const currentSalesName = currentSalesMetric ? getName(isEN, currentSalesMetric.enName, currentSalesMetric.cnName) : '';

    let processedParams = params;

    if (selectedSegment === 'forecast') {
      processedParams = swapPositions(params, legendData?.[1], legendData?.[0], 'seriesName')?.map((item: { seriesName: string; color: string; value: number|string; }) => {
        const correctedItem = {
          ...item,
          color: colorMap[item.seriesName] || item.color
        };
        
        if (item?.seriesName === legendData?.[1]) {
          const isEmpty = (isEmptyValue(item?.value) || item?.value === 0) && (isEmptyValue(params?.[0]?.value) || params?.[0]?.value === 0);
          const newValue = Number(item?.value || 0) + Number(params?.[0]?.value || 0);
          const isShowLower = visibleSeries?.includes(legendData?.[0]);
          return {
            ...correctedItem,
            value: isEmpty ? "" : isShowLower? newValue : Number(item?.value || 0),
          };
        }
      
        // 特殊处理 lifetime指标
        // 如果没数据则展示最新点的值
        if (item?.seriesName === legendData?.[2]) {
          return {
            ...correctedItem,
            value: item?.value || lastOriginData
          };
        }
      
        const isEmpty = (isEmptyValue(item?.value) || item?.value === 0);
        return {
          ...correctedItem,
          value: isEmpty ? "" : item?.value
        };
      });
    } else if (selectedSegment === 'competitor') {
      processedParams = params.map(param => {
        let displayValue = param.value;
    
        // 特殊处理 lifetime指标
        // 如果没数据则展示最新点的值
        if (param.seriesName === currentSalesName) {
          displayValue = param.value || lastOriginData;
        } 
    
        return {
          ...param,
          value: displayValue,
          color: colorMap[param.seriesName] || param.color,
        };
      });
    }

    // 构建表格数据
    const tableData = processedParams.map(param => ({
      seriesName: param.seriesName,
      value: param.value,
      color: param.color,
    }));

    return getEchartTooltipString({
      table: {
        data: tableData,
        columns: [
          {
            showIcon: true,
            key: 'seriesName',
            header: getName(isEN, tooltipDateMap?.[axisValue] || axisValue, dateNameMap?.[tooltipDateMap?.[axisValue] || axisValue]),   
          },
          {
            key: 'value',
            header: getName(isEN, tooltipConfig.unitEn, tooltipConfig.unitCn),
            dataFormatRule: formatRule,
            align: 'right',
          },
        ],
      },
    });
  };

  return { series, formatterFunc };
};
